import type { PublicationInboxAttention } from '@/lib/publicationInboxPresentation';

export type PublicationInboxFilterState = {
  search: string;
  tribunal: string;
  source: string;
  attention: PublicationInboxAttention | '';
  dateFrom: string;
  dateTo: string;
};

type Props = {
  value: PublicationInboxFilterState;
  onChange: (
    next: PublicationInboxFilterState
  ) => void;
};

const attentionOptions: Array<{
  value: PublicationInboxAttention;
  label: string;
}> = [
  { value: 'critical', label: 'Crítica' },
  { value: 'high', label: 'Alta' },
  { value: 'review', label: 'Revisão' },
  {
    value: 'informational',
    label: 'Informativa',
  },
  { value: 'resolved', label: 'Resolvida' },
];

const emptyPublicationInboxFilters:
  PublicationInboxFilterState = {
    search: '',
    tribunal: '',
    source: '',
    attention: '',
    dateFrom: '',
    dateTo: '',
  };

export default function PublicationInboxFilters({
  value,
  onChange,
}: Props) {
  const patch = (
    partial: Partial<PublicationInboxFilterState>
  ) =>
    onChange({
      ...value,
      ...partial,
    });

  return (
    <div className="rounded-2xl border border-background-200 bg-background-50 p-3">
      <div className="grid grid-cols-1 gap-2 lg:grid-cols-12">
        <label className="lg:col-span-4">
          <span className="sr-only">
            Buscar publicações
          </span>
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"></i>
            <input
              value={value.search}
              onChange={(event) =>
                patch({
                  search: event.target.value,
                })
              }
              className="w-full rounded-xl border border-background-200 bg-white py-2 pl-9 pr-3 text-sm"
              placeholder="Cliente, parte, processo ou texto da publicação"
            />
          </div>
        </label>

        <label className="lg:col-span-2">
          <span className="sr-only">
            Tribunal
          </span>
          <input
            value={value.tribunal}
            onChange={(event) =>
              patch({
                tribunal: event.target.value,
              })
            }
            className="w-full rounded-xl border border-background-200 bg-white px-3 py-2 text-sm"
            placeholder="Tribunal"
          />
        </label>

        <label className="lg:col-span-2">
          <span className="sr-only">
            Fonte
          </span>
          <input
            value={value.source}
            onChange={(event) =>
              patch({
                source: event.target.value,
              })
            }
            className="w-full rounded-xl border border-background-200 bg-white px-3 py-2 text-sm"
            placeholder="Fonte"
          />
        </label>

        <label className="lg:col-span-2">
          <span className="sr-only">
            Atenção
          </span>
          <select
            value={value.attention}
            onChange={(event) =>
              patch({
                attention:
                  event.target
                    .value as PublicationInboxAttention | '',
              })
            }
            className="w-full rounded-xl border border-background-200 bg-white px-3 py-2 text-sm"
          >
            <option value="">
              Toda atenção
            </option>
            {attentionOptions.map((option) => (
              <option
                key={option.value}
                value={option.value}
              >
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <div className="flex gap-2 lg:col-span-2">
          <button
            type="button"
            onClick={() =>
              onChange(
                emptyPublicationInboxFilters
              )
            }
            className="w-full rounded-xl border border-background-200 px-3 py-2 text-xs font-medium text-foreground-600 hover:bg-background-100"
          >
            Limpar filtros
          </button>
        </div>
      </div>

      <details className="mt-2">
        <summary className="cursor-pointer text-xs font-medium text-foreground-500">
          Filtros por período
        </summary>
        <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:max-w-xl">
          <label className="space-y-1">
            <span className="text-[11px] font-medium text-foreground-500">
              De
            </span>
            <input
              type="date"
              value={value.dateFrom}
              onChange={(event) =>
                patch({
                  dateFrom: event.target.value,
                })
              }
              className="w-full rounded-xl border border-background-200 bg-white px-3 py-2 text-sm"
            />
          </label>

          <label className="space-y-1">
            <span className="text-[11px] font-medium text-foreground-500">
              Até
            </span>
            <input
              type="date"
              value={value.dateTo}
              onChange={(event) =>
                patch({
                  dateTo: event.target.value,
                })
              }
              className="w-full rounded-xl border border-background-200 bg-white px-3 py-2 text-sm"
            />
          </label>
        </div>
      </details>
    </div>
  );
}